import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Stack from "@mui/material/Stack";
import { Tooltip, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import { selectTransactionHistory, selectResidentDetails, selectPaymentState, getCancelStatus } from "../sagas/residentpaymentsSlice";
import NumberFormat from "react-number-format";
import historyDetailsIcon from "../../../icons/paymentHistoryDetailsIcon.png";
import HelpIcon from "@mui/icons-material/Help";
import OnePaymentDetailsModal from "../../../shared/Modal/PaymentHistoryDetailsModal";
import HistoryIconError from "../../../icons/History_Icon.svg";
import PaymentHistoryIcon from "../../../icons/PaymentHistory.svg";
import SpinnerWithNoConfirmation from "../../../shared/Modal/spinnerWithNoConfirmation";
import { format } from "date-fns";
import { TransactionDTO } from "@inosio/payments-sdk";
import { convertCentsToDollars } from "../../../shared/util/converters";

export default function TransactionHistory() {
  const [open, setOpen] = React.useState(false);
  let transaction: TransactionDTO = {} as TransactionDTO;
  const [selectedRow, setSelectedRow] = React.useState(transaction);

  const handleClose = (event: object, reason: string) => {
    setOpen(false);
  };
  const handleOpen = (event: object, reason: string) => {
    setOpen(true);
  };
  const matchesMobile = useMediaQuery("(max-width:600px)");
  const matches900 = useMediaQuery("(min-width:900px)");
  const matches1200 = useMediaQuery("(max-width:1200px)");
  const dispatch = useDispatch();

  const actualRows = useSelector(selectTransactionHistory);
  const formatAmount = (cents: number) => {
    return cents / 100;
  };
  const residentsDetails = useSelector(selectResidentDetails);
  const { isFetchingPaymentHistoryFailed, isFetchingPaymentHistory, cancelStatusResponse } = useSelector(selectPaymentState);

  const getStatusTooltipText = (row: TransactionDTO) => {
    if (row.Status === 'Returned') {
      return row.ReturnTransactions?.Reason || '';
    }
    return '';
  }

  const getTypeText = (row: TransactionDTO) => {
    if (row.TransactionTypeId === 8) {
      return 'NSF';
    }
    if (row.Memo === 'Adhoc') return 'One-time';
    return row.Memo;
  }

  return (
    <React.Fragment>
      <Typography id="tableTitle" sx={{ flex: 1 }}>
        History 
        <Tooltip title="Click on the spyglass to see transaction details." placement="right">
          <HelpIcon sx={{ fontSize: 17, color: "#C4C4C4", cursor: "pointer" }} />
          </Tooltip>
      </Typography>
      {(isFetchingPaymentHistory && !actualRows) ? (
        <Stack
          justifyContent="center"
          alignItems="center"
          style={{
            height: "400px",
          }}
        >
          <SpinnerWithNoConfirmation />
        </Stack>
      ) : actualRows?.length !== 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell id="subTitle">Amount</TableCell>
              {!matchesMobile && <TableCell id="subTitle">Type</TableCell>}
              {!matchesMobile && !(matches900 && matches1200) && <TableCell id="subTitle">Date</TableCell>}
              <TableCell id="subTitle">Status</TableCell>
              {/* action */}
              <TableCell id="subTitle" >
                Details
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody
          //  style={{ left: "28px" }}
           >
            {actualRows &&
              actualRows?.map((row) => (
                <TableRow key={row.TransactionUuid}>
                  <TableCell id="content">
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={formatAmount(row.Amount)}
                      prefix="$"
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      allowNegative={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </TableCell>
                  {!matchesMobile && <TableCell id="content">{getTypeText(row)}</TableCell>}
                  {!matchesMobile && !(matches900 && matches1200) && <TableCell id="content">{row?.TransactionDate && format(new Date(row.TransactionDate), "MM/dd/yyyy")}</TableCell>}
                  <TableCell id="content">
                    {["Approved", "Issued", "Requested", "Denied"].includes(row.Refunds?.[0]?.Status || "") && row.Status === 'Completed' ? `Refund ${row.Refunds?.[0]?.Status}`
                    : <Tooltip title={getStatusTooltipText(row)} placement="right">
                        <span>{row.Status}</span>
                      </Tooltip>
                    }
                  </TableCell>
                  <TableCell id="content">
                    <img
                      style={{ cursor: "pointer" }}
                      src={historyDetailsIcon}
                      alt="details-icon"
                      onClick={() => {
                        // check if transaction can be cancelled
                        dispatch(getCancelStatus({ transactionUuid: row?.TransactionUuid }));
                        handleOpen({}, "openDetailsModel");
                        setSelectedRow(row);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      ) : actualRows?.length === 0 ? (
        <Stack
          justifyContent="center"
          alignItems="center"
          style={{
            height: "400px",
          }}
        >
          <img src={PaymentHistoryIcon} alt="history" width="226px" height="267px" />
          <Typography>History</Typography>
        </Stack>
      ) : isFetchingPaymentHistoryFailed ? (
        <Stack
          justifyContent="center"
          alignItems="center"
          style={{
            height: "400px",
          }}
        >
          <img src={HistoryIconError} alt="history" width="226px" height="267px" />
          <Typography>History</Typography>
        </Stack>
      ) : (
        <></>
      )}
      {selectedRow && Object.keys(selectedRow).length !== 0 ? (
        <OnePaymentDetailsModal
          open={open}
          closeCallback={handleClose}
          deviceWidth={window.innerWidth}
          paymentID={selectedRow.TransactionUuid}
          transactionDate={selectedRow?.TransactionDate}
          pmCompany={residentsDetails?.Lease?.CompanyName ?? ''}
          propertyName={residentsDetails && residentsDetails?.Lease?.PropertyName}
          leaseHolderName={residentsDetails && `${residentsDetails?.LastName} ${residentsDetails?.FirstName}`}
          paidAmount={convertCentsToDollars(selectedRow?.TransactionDetails?.map((transactionDetail) => {
            if (transactionDetail.EntityPaymentTypeId === 1) return 0;
            return transactionDetail.Amount;
          }).reduce((prev, nex) => prev + nex, 0))}
          fee={convertCentsToDollars(selectedRow?.TransactionDetails?.filter((transactionDetail) => transactionDetail.EntityPaymentTypeId === 1)?.[0]?.Amount) ?? 0}
          last4={selectedRow?.TransactionDetails?.[0]?.FromPayMethod?.AccountLastFour}
          canCancel={cancelStatusResponse}
          transactionMemo={selectedRow.Memo}
          transactionTypeId={selectedRow?.TransactionTypeId}
          refund={selectedRow?.Refunds?.[0]}
          returnTransaction={selectedRow?.ReturnTransactions && selectedRow?.ReturnTransactions}
          transactionStatus={selectedRow?.Status}
        />
      ) : (
        <></>
      )}
    </React.Fragment>
  );
}
